import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import {
  getWeightInGrams,
  getWeightInUnitFromGrams,
  getWeightPercentage,
} from "../utils";
import { HEADING_FONT } from "../constants/colors";

// Squarified treemap layout (Bruls et al.) — tiles keep aspect ratios close
// to square. Items must be sorted by value descending.
const layoutTreemap = (items, width, height) => {
  const total = items.reduce((acc, item) => acc + item.value, 0);
  if (!total || !width || !height) return [];
  const scaled = items.map((item) => ({
    ...item,
    area: (item.value / total) * width * height,
  }));

  const rects = [];
  let x = 0;
  let y = 0;
  let w = width;
  let h = height;
  let row = [];

  const worstRatio = (candidateRow, side) => {
    const sum = candidateRow.reduce((acc, r) => acc + r.area, 0);
    const max = Math.max(...candidateRow.map((r) => r.area));
    const min = Math.min(...candidateRow.map((r) => r.area));
    return Math.max(
      (side * side * max) / (sum * sum),
      (sum * sum) / (side * side * min)
    );
  };

  const layoutRow = (rowToPlace) => {
    const sum = rowToPlace.reduce((acc, r) => acc + r.area, 0);
    if (w >= h) {
      const rowWidth = sum / h;
      let ry = y;
      rowToPlace.forEach((r) => {
        const rh = r.area / rowWidth;
        rects.push({ ...r, x, y: ry, w: rowWidth, h: rh });
        ry += rh;
      });
      x += rowWidth;
      w -= rowWidth;
    } else {
      const rowHeight = sum / w;
      let rx = x;
      rowToPlace.forEach((r) => {
        const rw = r.area / rowHeight;
        rects.push({ ...r, x: rx, y, w: rw, h: rowHeight });
        rx += rw;
      });
      y += rowHeight;
      h -= rowHeight;
    }
  };

  let index = 0;
  while (index < scaled.length) {
    const side = Math.min(w, h);
    const item = scaled[index];
    if (
      row.length === 0 ||
      worstRatio([...row, item], side) <= worstRatio(row, side)
    ) {
      row.push(item);
      index += 1;
    } else {
      layoutRow(row);
      row = [];
    }
  }
  if (row.length) layoutRow(row);
  return rects;
};

// Palette colors are hsl(...) strings with normalized lightness; pick tile
// text color by that lightness so labels always clear contrast.
const getTileTextColor = (color) => {
  const match = /(\d+)%\)$/.exec(color || "");
  const lightness = match ? Number(match[1]) : 50;
  return lightness >= 56 ? "rgba(0, 0, 0, 0.78)" : "rgba(255, 255, 255, 0.94)";
};

const WeightBreakdown = ({ packDataArray, colorArray, unit }) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });

  const containerRef = React.useRef(null);
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const measure = () =>
      setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const rows = packDataArray.map((row, index) => ({
    ...row,
    grams: getWeightInGrams(row.weight, row.unit) * (row.quantity || 1),
    color: colorArray[index],
  }));
  const totalGrams = rows.reduce((acc, row) => acc + row.grams, 0) || 1;

  const formatValue = (grams) =>
    `${Number(getWeightInUnitFromGrams(grams, unit)).toFixed(
      2
    )} ${unit} · ${getWeightPercentage(grams, totalGrams, 1)}`;

  const tiles = layoutTreemap(
    rows.map((row) => ({ ...row, value: row.grams })),
    size.width,
    size.height
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        marginTop: isMobile ? "10px" : "8px",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {tiles.map((tile, index) => {
        const left = Math.round(tile.x);
        const top = Math.round(tile.y);
        const tileWidth = Math.max(Math.round(tile.x + tile.w) - left - 2, 1);
        const tileHeight = Math.max(Math.round(tile.y + tile.h) - top - 2, 1);
        const textColor = getTileTextColor(tile.color);
        const showLabel = tileWidth >= 64 && tileHeight >= 26;
        const showValue = showLabel && tileHeight >= 44;
        return (
          <Tooltip
            key={`tile-${tile.item}-${index}`}
            title={`${tile.item} — ${formatValue(tile.grams)}`}
          >
            <Box
              sx={{
                position: "absolute",
                left: `${left + 1}px`,
                top: `${top + 1}px`,
                width: `${tileWidth}px`,
                height: `${tileHeight}px`,
                backgroundColor: tile.color,
                borderRadius: `${Math.min(
                  4,
                  Math.max(1, Math.floor(Math.min(tileWidth, tileHeight) / 4))
                )}px`,
                overflow: "hidden",
                "&:hover": {
                  filter: "brightness(1.12)",
                },
              }}
            >
              {showLabel && (
                <Stack sx={{ padding: "5px 8px", minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 700,
                      fontSize: isMobile ? "13px" : "11.5px",
                      lineHeight: 1.25,
                      color: textColor,
                    }}
                  >
                    {tile.item}
                  </Typography>
                  {showValue && (
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 600,
                        fontSize: isMobile ? "12px" : "10.5px",
                        color: textColor,
                        opacity: 0.85,
                      }}
                    >
                      {formatValue(tile.grams)}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default WeightBreakdown;
